import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../../components/ui/button';
import { CdaLogo } from '../../../components/cda/CdaBrand';
import { LanguageBar } from '../../../i18n/cda';
import { ArrowRight, Building, Users, Award, CheckCircle, Heart } from 'lucide-react';

const LearningCenterPartnerships = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <Helmet>
        <title>Learning Center Partnerships | Partner with Care Comply for CDA Candidates</title>
        <meta name="description" content="Childcare centers in Texas can partner with Care Comply to connect with motivated CDA candidates ready to complete their 480 experience hours. Build your team with trained early childhood educators." />
        <link rel="canonical" href="https://carecomplycda.com/learning-center-partnerships" />
      </Helmet>

      <LanguageBar />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/cda"><CdaLogo size={34} /></Link>
          <Button onClick={() => navigate('/cda/centers')} className="bg-[#0d7d6f] hover:bg-[#0a6358]">
            Become a Partner
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="text-center mb-12">
          <p className="text-[#c4622d] font-semibold mb-2">For Childcare Centers</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Learning Center Partnerships
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Connect with motivated CDA candidates who are ready to learn, grow, and contribute to your early childhood program.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Why Partner with Care Comply?</h2>
          <p className="text-slate-600 mb-4">
            Finding qualified early childhood educators is one of the biggest challenges facing childcare centers in Texas. Many centers struggle with staffing shortages and high turnover rates.
          </p>
          <p className="text-slate-600 mb-4">
            Care Comply connects your center with CDA candidates who are actively working toward their credential and need placements to complete their required 480 hours of experience. These candidates are motivated, receiving ongoing training, and preparing for careers in early childhood education.
          </p>
          <p className="text-slate-600">
            By partnering with Care Comply, you get access to a pipeline of emerging talent while helping shape the next generation of early childhood professionals.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Users className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Qualified Candidates</h3>
            <p className="text-slate-600">Access candidates who have completed CDA training and are actively working toward their credential.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Award className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Workforce-Ready</h3>
            <p className="text-slate-600">Our candidates complete job readiness training including professionalism, communication, and workplace skills.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Building className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Build Your Team</h3>
            <p className="text-slate-600">Many CDA candidates who complete their hours at a center go on to become full-time employees.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Heart className="w-10 h-10 text-[#c4622d] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Support the Community</h3>
            <p className="text-slate-600">Help strengthen the early childhood workforce in your community by mentoring future educators.</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">How the Partnership Works</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">1</div>
              <div>
                <h3 className="font-semibold text-slate-900">Register as a Partner Center</h3>
                <p className="text-slate-600">Complete a simple registration form with your center's information and placement availability.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">2</div>
              <div>
                <h3 className="font-semibold text-slate-900">Get Matched with Candidates</h3>
                <p className="text-slate-600">CDA candidates in your area can view your center and request placement for their experience hours.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">3</div>
              <div>
                <h3 className="font-semibold text-slate-900">Host and Mentor</h3>
                <p className="text-slate-600">Candidates work at your center to gain experience while you evaluate them as potential future hires.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">4</div>
              <div>
                <h3 className="font-semibold text-slate-900">Build Your Pipeline</h3>
                <p className="text-slate-600">After candidates earn their CDA, you have first opportunity to offer them a permanent position.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">What We Look for in Partner Centers</h2>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <span className="text-slate-600">Licensed childcare facility in Texas</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <span className="text-slate-600">Willing to provide supervision and mentorship</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <span className="text-slate-600">Can verify and document candidate experience hours</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-[#0d7d6f] shrink-0 mt-0.5" />
              <span className="text-slate-600">Committed to quality early childhood education</span>
            </li>
          </ul>
        </div>

        <div className="bg-[#0d7d6f] rounded-2xl p-8 text-white text-center mb-8">
          <h2 className="text-2xl font-bold mb-3">Ready to Partner with Care Comply?</h2>
          <p className="mb-6 opacity-90">Join our network of Texas childcare centers connecting with motivated CDA candidates.</p>
          <Button onClick={() => navigate('/cda/centers')} className="bg-white text-[#0d7d6f] hover:bg-slate-100">
            Register Your Center <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="text-center text-sm text-slate-500">
          <p>Care Comply partners with childcare centers across Texas.</p>
          <p className="mt-2">
            <Link to="/cda-career-coaching-san-antonio" className="text-[#0d7d6f] hover:underline">Career Coaching</Link> • 
            <Link to="/cda-480-hours-support" className="text-[#0d7d6f] hover:underline ml-2">480-Hour Support</Link> • 
            <Link to="/about-dr-gabriela-ibarra-noriega" className="text-[#0d7d6f] hover:underline ml-2">About the Founder</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default LearningCenterPartnerships;
